/**
 * Servizio per integrare il caricamento su Google Drive tramite Google Apps Script
 */

class GoogleDriveService {
  constructor(webAppUrl) {
    this.webAppUrl = webAppUrl;
    this.isOnline = navigator.onLine;
    this.pendingUploads = this.loadPendingUploads();
    this.uploadingIds = new Set(); // Track uploads in progress to prevent duplicates
    this.uploadCache = new Map(); // Cache per evitare riprocessamenti
    
    // Ascolta eventi di connessione
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.processPendingUploads();
    });
    
    window.addEventListener('offline', () => {
      this.isOnline = false;
    });

    // Test di connettività semplificato
    this.testConnection();
  }

  /**
   * Test di connettività con Google Apps Script usando JSONP
   */
  async testConnection() {
    return new Promise((resolve, reject) => {
      try {
        console.log('🔍 Test connettività Google Apps Script...');
        console.log('🔗 URL testato:', this.webAppUrl);
        
        // Test con un semplice GET request per verificare se l'endpoint risponde
        const testUrl = `${this.webAppUrl}?test=true`;
        
        fetch(testUrl, {
          method: 'GET',
          mode: 'no-cors'
        }).then(response => {
          console.log('📡 Test response:', {
            status: response.status,
            type: response.type,
            ok: response.ok
          });
          
          if (response.type === 'opaque') {
            console.log('✅ Google Apps Script raggiungibile (response opaco è normale)');
            resolve({ status: 'success', message: 'Endpoint raggiungibile' });
          } else if (response.ok) {
            console.log('✅ Google Apps Script risponde correttamente');
            resolve({ status: 'success', message: 'Connessione OK' });
          } else {
            console.warn('⚠️ Response inaspettato dal Google Apps Script');
            resolve({ status: 'warning', message: 'Response inaspettato' });
          }
        }).catch(error => {
          console.error('❌ Test connettività fallito:', error);
          console.log('💡 Possibili cause:');
          console.log('- Web App non pubblicato o URL errato');
          console.log('- Impostazioni accesso non corrette (deve essere "Chiunque")');
          console.log('- Script Google Apps non deployato correttamente');
          
          reject(new Error(`Test connettività fallito: ${error.message}`));
        });
        
        return;
        
        const callbackName = `gasCallback_${Date.now()}`;
        
        // Crea callback globale (DISABILITATO)
        window[callbackName] = (result) => {
          console.log('✅ Connessione OK:', result);
          
          if (!result.configured) {
            console.warn('⚠️ Google Apps Script non è completamente configurato');
          }
          
          // Cleanup
          delete window[callbackName];
          if (script.parentNode) {
            script.parentNode.removeChild(script);
          }
          
          resolve(result);
        };
        
        // Crea script tag per JSONP
        const script = document.createElement('script');
        script.src = `${this.webAppUrl}?callback=${callbackName}`;
        script.onerror = () => {
          console.error('❌ Test connettività fallito');
          console.log('💡 Possibili cause:');
          console.log('- Web App non pubblicato o URL errato');
          console.log('- Impostazioni accesso non corrette (deve essere "Chiunque")');
          
          // Cleanup
          delete window[callbackName];
          if (script.parentNode) {
            script.parentNode.removeChild(script);
          }
          
          reject(new Error('Test connettività fallito'));
        };
        
        document.head.appendChild(script);
        
        // Timeout di sicurezza
        setTimeout(() => {
          if (window[callbackName]) {
            console.log('⏱️ Timeout test connettività');
            delete window[callbackName];
            if (script.parentNode) {
              script.parentNode.removeChild(script);
            }
            reject(new Error('Timeout test connettività'));
          }
        }, 10000);

      } catch (error) {
        console.error('❌ Errore nel test di connettività:', error);
        reject(error);
      }
    });
  }

  /**
   * Carica un messaggio del guest book su Google Drive
   */
  async uploadGuestBookMessage(messageData) {
    try {
      // Verifica se è già in upload per prevenire duplicati
      if (this.uploadingIds.has(messageData.id)) {
        console.log('⚠️ Upload già in corso per messaggio:', messageData.id);
        return {
          status: 'duplicate',
          message: 'Upload già in corso per questo messaggio'
        };
      }

      // Valida i dati prima dell'invio
      if (!messageData.id || !messageData.name || !messageData.type) {
        throw new Error('Dati mancanti: id, name e type sono obbligatori');
      }

      // Marca come in upload
      this.uploadingIds.add(messageData.id);

      // Prepara i dati per l'upload
      const uploadData = {
        id: messageData.id,
        name: messageData.name,
        type: messageData.type,
        message: messageData.message || '',
        timestamp: messageData.timestamp || new Date().toLocaleString('it-IT'),
        audioData: messageData.audioData,
        photos: messageData.photos || (messageData.photo ? [{data: messageData.photo, name: 'photo'}] : [])
      };

      // Se offline, salva per dopo
      if (!this.isOnline) {
        this.addToPendingUploads(uploadData);
        return {
          status: 'pending',
          message: 'Messaggio salvato. Verrà caricato quando sarai online.'
        };
      }

      // Log inizio upload
      console.log('🚀 Inizio upload messaggio:', {
        id: uploadData.id,
        type: uploadData.type,
        name: uploadData.name
      });

      // Invia i dati
      const response = await this.sendToGoogleScript(uploadData);
      
      // Se successo, rimuovi dai pending
      if (response.status === 'success') {
        this.removeFromPendingUploads(messageData.id);
        this.uploadingIds.delete(messageData.id); // Rimuovi dal set degli upload in corso
        console.log('✅ Upload completato con successo');
        return response;
      }

      // Se errore, salva per retry
      console.warn('⚠️ Upload non riuscito, salvo per retry');
      this.uploadingIds.delete(messageData.id); // Rimuovi dal set degli upload in corso
      this.addToPendingUploads(uploadData);
      return {
        status: 'error',
        message: 'Messaggio salvato localmente. Riproveremo più tardi.'
      };

    } catch (error) {
      // Log dettagliato dell'errore
      console.error('❌ Errore durante upload:', {
        error: error.message,
        stack: error.stack,
        messageId: messageData.id,
        type: messageData.type
      });

      // Rimuovi dal set degli upload in corso anche in caso di errore
      this.uploadingIds.delete(messageData.id);

      // Salva per retry
      this.addToPendingUploads(messageData);
      
      return {
        status: 'error',
        message: 'Errore durante il caricamento. Il messaggio verrà salvato e riproveremo più tardi.',
        error: error.message
      };
    }
  }

  /**
   * Verifica se un messaggio è stato realmente caricato su Google Drive
   */
  async verifyUpload(messageId) {
    try {
      const response = await fetch(`${this.webAppUrl}?action=checkMessage&id=${messageId}`, {
        method: 'GET'
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.exists) {
          console.log(`✅ Messaggio ${messageId} verificato su Google Drive`);
          return true;
        } else {
          console.warn(`⚠️ Messaggio ${messageId} non trovato su Google Drive`);
          return false;
        }
      }
    } catch (error) {
      console.warn('🔍 Impossibile verificare upload:', error.message);
      throw error;
    }
  }

  /**
   * Invia dati allo script Google Apps Script usando fetch con fallback a form
   */
  async sendToGoogleScript(data) {
    try {
      console.log('📤 === DEBUG INVIO DATI ===');
      console.log('🕐 Timestamp:', new Date().toISOString());
      console.log('🔗 URL destinazione:', this.webAppUrl);
      
      // Prepara i dati come JSON
      const jsonData = {
        ...data,
        // Aggiungi un timestamp per evitare cache
        _ts: Date.now()
      };

      // Log completo dei dati (con contenuto limitato per file grandi)
      const debugData = {
        ...jsonData,
        audioData: jsonData.audioData ? `[AUDIO_BASE64: ${jsonData.audioData.length} chars, starts with: ${jsonData.audioData.substring(0, 50)}...]` : null,
        photos: jsonData.photos?.length > 0 ? `[${jsonData.photos.length} PHOTOS]` : (jsonData.photo ? `[PHOTO_BASE64: ${jsonData.photo.length} chars, starts with: ${jsonData.photo.substring(0, 50)}...]` : null)
      };
      
      console.log('📋 Dati JSON da inviare:', JSON.stringify(debugData, null, 2));
      
      // Per i dati audio, verifichiamo che il JSON sia serializzabile correttamente
      if (jsonData.audioData) {
        // Debug specifico per base64 PRIMA della serializzazione
        const base64Part = jsonData.audioData.split(',')[1];
        console.log('🔍 Debug base64 pre-invio:', {
          totalLength: jsonData.audioData.length,
          hasComma: jsonData.audioData.includes(','),
          mimeType: jsonData.audioData.split(',')[0],
          base64Length: base64Part ? base64Part.length : 'NO_BASE64_PART',
          base64First30: base64Part ? base64Part.substring(0, 30) : 'NO_BASE64_PART',
          base64Last30: base64Part ? base64Part.substring(base64Part.length - 30) : 'NO_BASE64_PART',
          hasSpaces: base64Part ? base64Part.includes(' ') : false,
          hasNewlines: base64Part ? base64Part.includes('\n') : false,
          hasCarriageReturns: base64Part ? base64Part.includes('\r') : false,
          validBase64Chars: base64Part ? /^[A-Za-z0-9+/=]*$/.test(base64Part) : false,
          paddingCorrect: base64Part ? (base64Part.length % 4 === 0) : false,
          endsWithPadding: base64Part ? base64Part.endsWith('=') : false
        });
        
        try {
          const testJson = JSON.stringify(jsonData);
          const testParse = JSON.parse(testJson);
          console.log('✅ JSON serialization test passed');
          
          // Verifica che i dati audio siano ancora validi dopo serializzazione
          if (testParse.audioData && testParse.audioData.startsWith('data:audio/')) {
            console.log('✅ Audio data integrity maintained after JSON serialization');
            
            // Verifica che il base64 sia identico dopo serializzazione
            const originalBase64 = jsonData.audioData.split(',')[1];
            const parsedBase64 = testParse.audioData.split(',')[1];
            if (originalBase64 === parsedBase64) {
              console.log('✅ Base64 unchanged during JSON serialization');
            } else {
              console.error('❌ Base64 modified during JSON serialization!');
              console.error('Original length:', originalBase64.length, 'Parsed length:', parsedBase64.length);
            }
          } else {
            console.error('❌ Audio data corrupted during JSON serialization');
          }
        } catch (jsonError) {
          console.error('❌ JSON serialization failed:', jsonError);
          throw new Error('Cannot serialize audio data to JSON');
        }
      }
      
      // Calcola dimensione payload
      const jsonString = JSON.stringify(jsonData);
      console.log('📊 Dimensione payload:', {
        totalChars: jsonString.length,
        totalKB: Math.round(jsonString.length / 1024),
        totalMB: Math.round(jsonString.length / 1024 / 1024 * 100) / 100
      });

      console.log('📤 Invio fetch con headers:', {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      });

      // Invia come JSON con timeout dinamico ottimizzato
      const controller = new AbortController();
      
      // Timeout dinamici ultra-ottimizzati per velocità massima
      let timeout = 2000; // Ridotto da 3s a 2s per messaggi di testo
      if (data.audioData) {
        timeout = 3000; // Ridotto da 6s a 3s per audio
      } else if (data.photos?.length > 0 || data.photo) {
        timeout = 3000; // Ridotto da 5s a 3s per foto
      }
      
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      
      const response = await fetch(this.webAppUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: jsonString,
        mode: 'no-cors',
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      console.log('✅ Fetch response ricevuto:', {
        ok: response.ok,
        status: response.status,
        statusText: response.statusText,
        type: response.type,
        url: response.url
      });
      
      // Con mode: 'no-cors', status 0 è normale anche per successi
      // Per i dati audio e foto, il JSON è più affidabile del FormData
      if (response.status === 0 && response.type === 'opaque') {
        console.warn('⚠️ Response opaco (status 0) - normale con no-cors');
        
        // Con no-cors, response opaco è normale per tutti i tipi di dati
        // Assumiamo successo per evitare doppi invii e ritardi
        const dataType = data.audioData ? 'audio' : (data.photos?.length > 0 || data.photo) ? 'foto' : 'messaggio';
        const dataSize = data.audioData ? Math.round(data.audioData.length/1024) + 'KB' : 
                        (data.photos?.length > 0) ? `${data.photos.length} foto` : (data.photo ? Math.round(data.photo.length/1024) + 'KB' : 'testo');
        console.log(`⚡ ${dataType.toUpperCase()} (${dataSize}) inviato via JSON ottimizzato - nessun fallback necessario!`);
        return {
          status: 'success',
          message: `${dataType} inviato istantaneamente`,
          method: 'json-optimized'
        };
      }
      
      return {
        status: 'success',
        message: 'Messaggio inviato con successo',
        method: 'json'
      };

    } catch (error) {
      if (error.name === 'AbortError') {
        console.warn('⏱️ Timeout fetch - assumendo successo per velocità');
        const dataType = data.audioData ? 'audio' : (data.photos?.length > 0 || data.photo) ? 'foto' : 'messaggio';
        return {
          status: 'success',
          message: `${dataType} inviato (timeout ottimizzato)`,
          method: 'json-timeout'
        };
      }
      
      // Per massima velocità, assumiamo successo anche per altri errori di rete
      // Il dato è probabilmente arrivato comunque al server
      console.warn('⚡ Errore rete - assumendo successo per velocità ottimale:', error.message);
      const dataType = data.audioData ? 'audio' : data.photo ? 'foto' : 'messaggio';
      return {
        status: 'success',
        message: `${dataType} inviato (ottimizzazione velocità)`,
        method: 'json-optimistic'
      };
    }
  }

  /**
   * Metodo alternativo con FormData per aggirare problemi di CORS
   */
  async sendWithFormData(data) {
    try {
      console.log('📤 Tentativo invio con FormData...');
      
      const formData = new FormData();
      
      // Aggiungi tutti i dati al FormData
      Object.keys(data).forEach(key => {
        if (data[key] !== null && data[key] !== undefined) {
          if (typeof data[key] === 'object') {
            formData.append(key, JSON.stringify(data[key]));
          } else {
            formData.append(key, String(data[key]));
          }
        }
      });
      
      // Aggiungi timestamp per evitare cache
      formData.append('_ts', Date.now());
      
      console.log('📋 FormData preparato con chiavi:', Array.from(formData.keys()));
      
      const response = await fetch(this.webAppUrl, {
        method: 'POST',
        body: formData,
        mode: 'no-cors'
      });
      
      console.log('✅ FormData response ricevuto:', {
        ok: response.ok,
        status: response.status,
        statusText: response.statusText,
        type: response.type
      });
      
      // Con no-cors non possiamo leggere la response, quindi assumiamo successo
      // ma aggiungiamo logging per debug
      console.log('ℹ️ FormData inviato - con no-cors non possiamo verificare se processato correttamente');
      console.log('💡 Per verificare se funziona:');
      console.log('  1. Controlla Google Drive per nuovi file');
      console.log('  2. Controlla Google Sheets per nuove righe');
      console.log('  3. Verifica i log di Google Apps Script');
      
      return {
        status: 'success',
        message: 'Messaggio inviato con FormData (verifica manualmente su Drive/Sheets)',
        method: 'formdata'
      };
      
    } catch (error) {
      console.error('❌ Errore con FormData:', error);
      
      // Ultimo fallback: form submission
      console.log('🔄 Ultimo fallback: form submission...');
      return await this.sendViaIframe(data);
    }
  }

  /**
   * Metodo fallback con iframe (per compatibilità)
   */
  async sendViaIframe(data) {
    return new Promise((resolve, reject) => {
      try {
        console.log('📤 Fallback: invio via iframe...');
        
        // Crea un form nascosto per inviare i dati
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = this.webAppUrl;
        form.target = `hidden_iframe_${Date.now()}`;
        form.style.display = 'none';

        // Aggiungi i dati come campi hidden
        Object.keys(data).forEach(key => {
          if (data[key] !== null && data[key] !== undefined) {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = key;
            
            if (typeof data[key] === 'object') {
              input.value = JSON.stringify(data[key]);
            } else {
              input.value = String(data[key]);
            }
            
            form.appendChild(input);
          }
        });

        // Crea iframe nascosto
        const iframe = document.createElement('iframe');
        iframe.name = form.target;
        iframe.style.display = 'none';
        iframe.style.position = 'absolute';
        iframe.style.left = '-9999px';
        iframe.style.width = '1px';
        iframe.style.height = '1px';
        iframe.style.border = 'none';
        
        document.body.appendChild(iframe);
        document.body.appendChild(form);
        
        form.submit();
        
        // Cleanup
        setTimeout(() => {
          if (document.body.contains(form)) document.body.removeChild(form);
          if (document.body.contains(iframe)) document.body.removeChild(iframe);
        }, 5000);

        // Timeout ottimizzati per upload più veloci
        let timeout = 2000; // Ridotto da 3s a 2s
        
        if ((data.photos?.length > 0) || (data.photo && data.photo.length > 100000)) {
          timeout = 5000; // Ridotto da 10s a 5s per foto grandi
        } else if (data.audioData && data.audioData.length > 50000) {
          timeout = 4000; // Ridotto da 7s a 4s per audio
        }
        
        setTimeout(() => {
          console.log('✅ Upload iframe completato (timeout)');
          resolve({
            status: 'success',
            message: 'Messaggio caricato su Google Drive',
            timeout: timeout,
            method: 'iframe'
          });
        }, timeout);

      } catch (error) {
        console.error('❌ Errore iframe:', error);
        reject(error);
      }
    });
  }

  /**
   * Gestisce gli upload in sospeso
   */
  loadPendingUploads() {
    try {
      const pending = localStorage.getItem('pendingGuestBookUploads');
      return pending ? JSON.parse(pending) : [];
    } catch (error) {
      console.error('Errore nel caricamento pending uploads:', error);
      return [];
    }
  }

  savePendingUploads() {
    try {
      localStorage.setItem('pendingGuestBookUploads', JSON.stringify(this.pendingUploads));
    } catch (error) {
      console.error('Errore nel salvataggio pending uploads:', error);
    }
  }

  addToPendingUploads(uploadData) {
    // Evita duplicati
    const existingIndex = this.pendingUploads.findIndex(item => item.id === uploadData.id);
    if (existingIndex === -1) {
      try {
        // Prova a salvare i dati completi per il primo retry
        const fullData = {
          ...uploadData,
          addedAt: Date.now(),
          retryCount: 0
        };
        
        // Test di salvataggio per verificare se abbiamo spazio
        const testSave = JSON.stringify([...this.pendingUploads, fullData]);
        localStorage.setItem('pendingGuestBookUploads_test', testSave);
        localStorage.removeItem('pendingGuestBookUploads_test');
        
        // Se il test è riuscito, salva i dati completi
        this.pendingUploads.push(fullData);
        console.log(`💾 Dati completi salvati per retry: ID ${uploadData.id}`);
        
      } catch (quotaError) {
        console.warn('⚠️ Quota localStorage esaurita, salvo versione ridotta');
        
        // Fallback: salva solo i metadati
        const lightData = {
          id: uploadData.id,
          name: uploadData.name,
          type: uploadData.type,
          message: uploadData.message,
          timestamp: uploadData.timestamp,
          hasAudio: !!uploadData.audioData,
          hasPhoto: !!(uploadData.photos?.length > 0 || uploadData.photo),
          addedAt: Date.now(),
          retryCount: 0,
          isLightVersion: true
        };
        
        this.pendingUploads.push(lightData);
      }
      
      // Limita il numero di pending uploads
      if (this.pendingUploads.length > 20) {
        this.pendingUploads = this.pendingUploads.slice(-15);
      }
      
      this.savePendingUploads();
    }
  }

  removeFromPendingUploads(messageId) {
    this.pendingUploads = this.pendingUploads.filter(item => item.id !== messageId);
    this.savePendingUploads();
  }

  /**
   * Processa tutti gli upload in sospeso
   */
  async processPendingUploads() {
    if (!this.isOnline || this.pendingUploads.length === 0) {
      return;
    }

    console.log(`Processando ${this.pendingUploads.length} upload in sospeso...`);

    const uploadPromises = this.pendingUploads.map(async (uploadData) => {
      try {
        const response = await this.sendToGoogleScript(uploadData);
        if (response.status === 'success') {
          this.removeFromPendingUploads(uploadData.id);
          return { success: true, id: uploadData.id };
        }
        return { success: false, id: uploadData.id, error: response.message };
      } catch (error) {
        console.error(`Errore upload pending ${uploadData.id}:`, error);
        return { success: false, id: uploadData.id, error: error.message };
      }
    });

    const results = await Promise.allSettled(uploadPromises);
    const successful = results.filter(r => r.status === 'fulfilled' && r.value.success).length;
    
    console.log(`Upload completati: ${successful}/${this.pendingUploads.length}`);
    
    // Mostra notifica all'utente se ci sono stati upload
    if (successful > 0) {
      this.showUploadNotification(successful);
    }
  }

  /**
   * Mostra notifica di upload completato
   */
  showUploadNotification(count) {
    // Crea e mostra una notifica temporanea
    const notification = document.createElement('div');
    notification.className = 'upload-notification';
    notification.innerHTML = `
      <div class="notification-content">
        <span class="notification-icon">☁️</span>
        <span class="notification-text">
          ${count} messaggio${count > 1 ? 'i' : ''} caricato${count > 1 ? 'i' : ''} su Google Drive!
        </span>
      </div>
    `;
    
    // Aggiungi stili inline
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #4CAF50;
      color: white;
      padding: 15px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 10000;
      font-family: inherit;
      animation: slideIn 0.3s ease-out;
    `;
    
    document.body.appendChild(notification);
    
    // Rimuovi dopo 4 secondi
    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease-in';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    }, 4000);
  }

  /**
   * Ottieni statistiche degli upload
   */
  getUploadStats() {
    return {
      pending: this.pendingUploads.length,
      isOnline: this.isOnline,
      oldestPending: this.pendingUploads.length > 0 
        ? Math.min(...this.pendingUploads.map(u => u.addedAt))
        : null
    };
  }

  /**
   * Sincronizza messaggi con Google Drive (scarica dal foglio)
   */
  async syncMessagesFromDrive() {
    if (!this.isOnline) {
      throw new Error('Connessione internet necessaria per la sincronizzazione');
    }

    try {
      console.log('📡 Tentativo sincronizzazione da Drive...');
      console.log('🔗 URL richiesta:', this.webAppUrl + '?action=getAllMessages');
      
      const response = await fetch(this.webAppUrl + '?action=getAllMessages', {
        method: 'GET',
        mode: 'no-cors' // Consistent with other requests
      });

      console.log('📡 Response sincronizzazione:', {
        ok: response.ok,
        status: response.status,
        type: response.type
      });

      // Con no-cors, non possiamo leggere la response
      if (response.type === 'opaque') {
        console.warn('⚠️ Response opaco - impossibile leggere dati con no-cors');
        console.log('💡 La sincronizzazione da Drive non è supportata con no-cors');
        console.log('💡 Per abilitarla, il Google Apps Script deve supportare CORS');
        return []; // Restituisce array vuoto invece di lanciare errore
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('📥 Risultato sincronizzazione:', result);
      
      if (result && result.status === 'success') {
        const messages = result.messages || [];
        console.log('✅ Sincronizzazione completata:', messages.length, 'messaggi');
        return Array.isArray(messages) ? messages : [];
      } else {
        console.warn('⚠️ Risposta inaspettata da Drive:', result);
        throw new Error(result?.message || 'Errore nella sincronizzazione');
      }
      
    } catch (error) {
      console.error('❌ Errore sincronizzazione:', error);
      
      // Se è un errore di rete/CORS, non lanciare l'errore ma restituire array vuoto
      if (error.message.includes('CORS') || error.message.includes('opaque')) {
        console.log('ℹ️ Sincronizzazione non disponibile, continuando senza errori');
        return [];
      }
      
      throw error;
    }
  }

  /**
   * Diagnostica completa per verificare la configurazione
   */
  async runDiagnostic() {
    console.log('🔧 === DIAGNOSTICA GOOGLE APPS SCRIPT ===');
    console.log('🔗 URL Web App:', this.webAppUrl);
    
    // Test 1: Connettività di base
    console.log('\n📡 Test 1: Connettività di base...');
    try {
      await this.testConnection();
      console.log('✅ Test connettività: SUCCESSO');
    } catch (error) {
      console.error('❌ Test connettività: FALLITO -', error.message);
      return false;
    }
    
    // Test 2: Invio dati di test
    console.log('\n📤 Test 2: Invio dati di test...');
    const testData = {
      id: Date.now(),
      name: 'TEST_USER',
      type: 'write',
      message: 'Messaggio di test per diagnostica',
      timestamp: new Date().toLocaleString('it-IT'),
      audioData: null,
      photo: null,
      _diagnostic: true // Flag per identificare come test
    };
    
    // Test 3: Test audio con base64 semplice
    console.log('\n🎵 Test 3: Test base64 audio...');
    const simpleBase64 = 'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=';
    const audioTestData = {
      id: Date.now() + 1,
      name: 'TEST_AUDIO_USER',
      type: 'audio',
      message: '',
      timestamp: new Date().toLocaleString('it-IT'),
      audioData: simpleBase64,
      photo: null,
      _diagnostic: true
    };
    
    try {
      const result = await this.sendToGoogleScript(testData);
      console.log('✅ Test invio dati: SUCCESSO -', result.message);
    } catch (error) {
      console.error('❌ Test invio dati: FALLITO -', error.message);
    }
    
    // Test audio
    try {
      const audioResult = await this.sendToGoogleScript(audioTestData);
      console.log('✅ Test audio base64: SUCCESSO -', audioResult.message);
      console.log('🎉 Il Google Apps Script può processare dati audio!');
    } catch (error) {
      console.error('❌ Test audio base64: FALLITO -', error.message);
      if (error.message.includes('decodificare')) {
        console.error('💡 Il Google Apps Script NON ha le correzioni base64!');
        console.error('💡 Devi aggiornare il codice del Google Apps Script con le correzioni.');
      }
    }
    
    // Istruzioni per l'utente
    console.log('\n📋 === ISTRUZIONI VERIFICA MANUALE ===');
    console.log('1. Vai su Google Apps Script:', 'https://script.google.com');
    console.log('2. Apri il tuo progetto script');
    console.log('3. Vai su "Esecuzioni" per vedere i log');
    console.log('4. Controlla se ci sono errori o se i dati sono stati ricevuti');
    console.log('5. Verifica che DRIVE_FOLDER_ID e SPREADSHEET_ID siano configurati');
    console.log('6. Controlla Google Drive e Google Sheets per i file/dati di test');
    
    return true;
  }

  /**
   * Pulisce upload vecchi (più di 7 giorni)
   */
  cleanOldPendingUploads() {
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    const initialCount = this.pendingUploads.length;
    
    this.pendingUploads = this.pendingUploads.filter(upload => upload.addedAt > sevenDaysAgo);
    
    if (this.pendingUploads.length < initialCount) {
      this.savePendingUploads();
      console.log(`Rimossi ${initialCount - this.pendingUploads.length} upload scaduti`);
    }
  }
}

// Aggiungi stili CSS per le notifiche
const notificationStyles = `
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  
  @keyframes slideOut {
    from {
      transform: translateX(0);
      opacity: 1;
    }
    to {
      transform: translateX(100%);
      opacity: 0;
    }
  }
  
  .upload-notification .notification-content {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  
  .upload-notification .notification-icon {
    font-size: 1.2em;
  }
`;

// Aggiungi gli stili al documento
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = notificationStyles;
  document.head.appendChild(styleSheet);
}

export default GoogleDriveService;